import { useMemo } from 'react';
import type { ChatbotMessage } from '../../types/chatbot.types';

interface MessageBubbleProps {
  message: ChatbotMessage;
  isStreaming?: boolean;
  index?: number;
}

function formatMessageDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const LINK_CLASS =
  'underline underline-offset-2 font-medium text-primary hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 transition-colors';
const CODE_CLASS =
  'rounded-lg bg-slate-200 px-2 py-1 text-sm font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300';

// Marca los fragmentos ya convertidos a HTML mientras se procesa el resto.
// U+E000 es de uso privado: no aparece en un mensaje legítimo y se elimina de la entrada.
const HOLD = '\uE000';
const HOLD_PATTERN = /\uE000(\d+)\uE000/g;
const BARE_URL_PATTERN = /https?:\/\/[^\s<>"'`\uE000]+/g;
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

type Hold = (html: string) => string;

function applyInlineStyles(escaped: string): string {
  return (
    escaped
      // Bold: **text** or __text__
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic: *text* or _text_
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
  );
}

function anchor(url: string, labelHtml: string): string {
  return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" class="${LINK_CLASS}">${labelHtml}</a>`;
}

/**
 * Markdown mínimo del chat. Código y links se apartan antes de aplicar
 * negritas/cursivas: si no, un `_` dentro de una URL se leía como cursiva y
 * rompía el link (típico en links de pago). Todo el texto se escapa una sola vez.
 */
function parseSimpleMarkdown(content: string): string {
  const held: string[] = [];
  const hold: Hold = html => `${HOLD}${held.push(html) - 1}${HOLD}`;

  const withoutCode = content
    .slice(0, 20_000)
    .split(HOLD)
    .join('')
    .replace(/`([^`]+)`/g, (_match, code: string) =>
      hold(`<code class="${CODE_CLASS}">${escapeHtml(code)}</code>`)
    );

  const withoutLinks = holdMarkdownLinks(withoutCode, hold).replace(BARE_URL_PATTERN, raw => {
    const url = raw.replace(TRAILING_PUNCTUATION, '');
    return hold(anchor(url, escapeHtml(url))) + raw.slice(url.length);
  });

  // Un fragmento apartado puede contener otro anterior (código dentro del texto de un link).
  const restore = (html: string): string =>
    html.replace(HOLD_PATTERN, (_match, index: string) => restore(held[Number(index)] ?? ''));

  return restore(applyInlineStyles(escapeHtml(withoutLinks))).replace(/\n/g, '<br />');
}

function holdMarkdownLinks(content: string, hold: Hold): string {
  let remaining = content;
  let result = '';

  while (remaining.length > 0) {
    const nextLink = extractMarkdownLink(remaining);
    if (!nextLink) {
      result += remaining;
      break;
    }

    result += remaining.slice(0, nextLink.startIndex);
    if (nextLink.label && isSafeHttpUrl(nextLink.url)) {
      result += hold(anchor(nextLink.url, applyInlineStyles(escapeHtml(nextLink.label))));
    } else {
      result += remaining.slice(nextLink.startIndex, nextLink.endIndex + 1);
    }
    remaining = remaining.slice(nextLink.endIndex + 1);
  }

  return result;
}

function isSafeHttpUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://');
}

type MarkdownLinkData = {
  startIndex: number;
  endIndex: number;
  label: string;
  url: string;
};

function extractMarkdownLink(value: string): MarkdownLinkData | null {
  // Un corchete suelto ("[nota] mirá [el link](https://…)") no debe impedir
  // encontrar el link que viene después.
  let textStart = value.indexOf('[');
  while (textStart !== -1) {
    const textEnd = value.indexOf(']', textStart + 1);
    if (textEnd === -1) {
      return null;
    }

    if (value[textEnd + 1] === '(') {
      const urlStart = textEnd + 2;
      const urlEnd = value.indexOf(')', urlStart);
      if (urlEnd === -1) {
        return null;
      }
      return {
        startIndex: textStart,
        endIndex: urlEnd,
        label: value.slice(textStart + 1, textEnd),
        url: value.slice(urlStart, urlEnd).trim(),
      };
    }

    textStart = value.indexOf('[', textStart + 1);
  }

  return null;
}

function hasFileExtension(url: string, extensions: string[]): boolean {
  const safeUrl = url.toLowerCase();
  return extensions.some(ext => safeUrl.endsWith(ext));
}

function TypingIndicator() {
  return (
    <div className='flex items-center gap-1.5 px-1'>
      <span className='h-2 w-2 animate-bounce rounded-full bg-violet-500 [animation-delay:-0.3s]' />
      <span className='h-2 w-2 animate-bounce rounded-full bg-violet-500 [animation-delay:-0.15s]' />
      <span className='h-2 w-2 animate-bounce rounded-full bg-violet-500' />
    </div>
  );
}

export function MessageBubble({ message, isStreaming, index = 0 }: MessageBubbleProps) {
  const isUser = message.sender === 'USER';
  const formattedTime = useMemo(() => formatMessageDateTime(message.createdAt), [message.createdAt]);
  const parsedContent = useMemo(() => parseSimpleMarkdown(message.content), [message.content]);
  const showTypingIndicator = !isUser && isStreaming && !message.content;
  const shouldShowContent =
    !(message.contentType === 'AUDIO' && message.fileUrl) && message.content.trim().length > 0;

  // Calculate stagger animation delay based on index
  const animationDelay = index ? Math.min(index * 50, 500) : 0;

  const renderAttachment = () => {
    if (!message.fileUrl) return null;

    // Simple detection based on extension if contentType is generic or missing
    const isImage =
      message.contentType === 'IMAGE' ||
      hasFileExtension(message.fileUrl, ['.jpg', '.jpeg', '.png', '.gif', '.webp']);
    const isAudio =
      message.contentType === 'AUDIO' ||
      hasFileExtension(message.fileUrl, ['.mp3', '.wav', '.ogg', '.m4a']);

    if (isImage) {
      return (
        <div className='mb-2 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700'>
          <img
            src={message.fileUrl}
            alt='Adjunto'
            className='max-h-60 w-full object-cover'
            loading='lazy'
          />
          <a
            href={message.fileUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='block bg-slate-50 px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
          >
            Ver original
          </a>
        </div>
      );
    }

    if (isAudio) {
      return (
        <div className='mb-2 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800'>
          <div className='mb-2 flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
              className='h-4 w-4 text-violet-500'
              aria-hidden='true'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.621 0-1.125-.504-1.125-1.125v-4.5c0-.621.504-1.125 1.125-1.125H6.75z'
              />
            </svg>
            <span>Mensaje de audio</span>
          </div>
          {/* `controls` nativo de HTML5 incluye play/pausa, scrub bar y tiempo;
              `preload="metadata"` permite mostrar la duración total sin
              descargar el blob completo. Mantener altura natural para que el
              control de seek no quede recortado en Firefox/Safari. */}
          <audio
            controls
            preload='metadata'
            className='w-full'
            src={message.fileUrl}
            aria-label='Reproductor de audio'
          />
          <div className='mt-1 text-right'>
            <a
              href={message.fileUrl}
              download
              target='_blank'
              rel='noopener noreferrer'
              className='text-xs text-primary hover:underline dark:text-violet-400'
            >
              Descargar audio
            </a>
          </div>
        </div>
      );
    }

    // Generic file
    const filename = message.fileUrl.split('/').pop() || 'Archivo adjunto';
    return (
      <div className='mb-2 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700'>
        <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-violet-100 text-primary dark:bg-violet-900/30 dark:text-violet-400'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
            stroke='currentColor'
            className='w-6 h-6'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'
            />
          </svg>
        </div>
        <div className='flex-1 overflow-hidden'>
          <p className='truncate text-sm font-medium text-slate-700 dark:text-slate-200'>
            {filename}
          </p>
          <a
            href={message.fileUrl}
            download
            target='_blank'
            rel='noopener noreferrer'
            className='text-xs text-primary hover:underline dark:text-violet-400'
          >
            Descargar
          </a>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`flex w-full animate-in fade-in-0 slide-in-from-bottom-4 duration-500`}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <div
        className={`flex w-full max-w-[85%] sm:max-w-[75%] ${isUser ? 'ml-auto flex-row-reverse' : 'mr-auto flex-row'}`}
      >
        {/* Avatar for bot messages */}
        {!isUser && (
          <div className='mr-3 flex h-9 w-9 shrink-0 self-start items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-primary to-primary-700 shadow-md'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
              className='ml-1 h-5 w-5 text-white'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z'
              />
            </svg>
          </div>
        )}

        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} space-y-1`}>
          {/* Message bubble - SOLID BACKGROUND for better readability */}
          <div
            className={`max-w-full rounded-2xl px-4 py-2.5 shadow-sm transition-all duration-200 ${
              isUser
                ? 'rounded-br-md bg-gradient-to-br from-primary to-primary-700 text-white'
                : 'rounded-bl-md border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100'
            }`}
          >
            <div className='text-sm leading-relaxed'>
              {renderAttachment()}
              {showTypingIndicator ? (
                <TypingIndicator />
              ) : !shouldShowContent ? null : (
                <div
                  className='prose prose-sm max-w-none break-words'
                  dangerouslySetInnerHTML={{ __html: parsedContent }}
                />
              )}
            </div>
          </div>

          {/* Timestamp with better contrast */}
          <p className={`text-xs font-medium text-slate-400 ${isUser ? 'mr-1' : 'ml-1'} px-1`}>
            {formattedTime}
          </p>
        </div>

        {/* User avatar indicator - simpler */}
        {isUser && (
          <div className='ml-3 flex h-9 w-9 shrink-0 self-start items-center justify-center rounded-full border-2 border-white bg-slate-700 shadow-md dark:bg-slate-600'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
              className='h-5 w-5 text-white'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z'
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
